import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useSubmitFeedback, useMyFeedback, captureFeedbackMetadata } from '@/hooks/use-feedback';
import {
  FeedbackType,
  FeedbackSeverity,
  FeedbackStatus,
  MyFeedbackItem,
  MAX_FEEDBACK_ATTACHMENTS,
  MAX_FEEDBACK_ATTACHMENT_BYTES,
  FEEDBACK_ATTACHMENT_ACCEPT,
  FEEDBACK_ATTACHMENT_MIME_ALLOWLIST,
} from '@/services/feedback';
import { fileUploadApi } from '@/services/file-upload';
import { authUtils } from '@/utils/auth';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

// Sprint 38 + Feedback Functionality sprint — Feedback modal.
// Richer bug fields + multi-file attachments (images + PDF).

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Tab = 'submit' | 'mine';

interface PendingFile {
  localId: string;
  file: File;
  uploadId?: string;
  uploading: boolean;
  error?: string;
}

export function FeedbackModal({ open, onOpenChange }: FeedbackModalProps) {
  const [tab, setTab] = React.useState<Tab>('submit');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Feedback</DialogTitle>
        </DialogHeader>

        <div className="flex border-b-[1.5px] border-border-subtle -mx-6 px-6">
          <TabButton label="Submit" active={tab === 'submit'} onClick={() => setTab('submit')} />
          <TabButton label="My Submissions" active={tab === 'mine'} onClick={() => setTab('mine')} />
        </div>

        {/* Keep SubmitForm mounted across tab switches so draft + uploads survive. */}
        <div className={tab === 'submit' ? undefined : 'hidden'} aria-hidden={tab !== 'submit'}>
          <SubmitForm onSubmitted={() => onOpenChange(false)} />
        </div>
        {tab === 'mine' && <MySubmissionsList />}
      </DialogContent>
    </Dialog>
  );
}

function TabButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative px-4 py-2.5 -mb-[1.5px] font-public-sans text-xs font-bold uppercase tracking-wider',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'text-foreground border-b-[2px] border-primary'
          : 'text-foreground-soft hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

function SubmitForm({ onSubmitted }: { onSubmitted: () => void }) {
  const navigate = useNavigate();
  const submit = useSubmitFeedback();

  const [type, setType] = React.useState<FeedbackType>('SUGGESTION');
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [severity, setSeverity] = React.useState<FeedbackSeverity>('MEDIUM');
  const [stepsToReproduce, setStepsToReproduce] = React.useState('');
  const [expected, setExpected] = React.useState('');
  const [actual, setActual] = React.useState('');
  const [whyItMatters, setWhyItMatters] = React.useState('');
  const [pendingFiles, setPendingFiles] = React.useState<PendingFile[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  // localIds removed while an upload is still in flight — delete the object
  // when the request finishes so mid-upload Remove cannot orphan storage.
  const cancelledUploadsRef = React.useRef<Set<string>>(new Set());

  const uploading = pendingFiles.some((f) => f.uploading);
  const valid =
    title.trim().length > 0 &&
    description.trim().length > 0 &&
    !uploading &&
    pendingFiles.every((f) => f.uploadId && !f.error);

  const uploadOne = async (file: File) => {
    const localId = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;
    setPendingFiles((prev) => [...prev, { localId, file, uploading: true }]);

    const orgId = authUtils.getOrganizationId();
    if (!orgId) {
      setPendingFiles((prev) =>
        prev.map((p) =>
          p.localId === localId
            ? { ...p, uploading: false, error: 'Not signed in to an organization' }
            : p,
        ),
      );
      return;
    }

    try {
      const res = await fileUploadApi.uploadFile(file, 'GENERAL', 'OTHER', orgId);
      if (cancelledUploadsRef.current.has(localId)) {
        cancelledUploadsRef.current.delete(localId);
        void fileUploadApi.deleteFile(res.id).catch(() => undefined);
        return;
      }
      setPendingFiles((prev) =>
        prev.map((p) =>
          p.localId === localId
            ? { ...p, uploading: false, uploadId: res.id }
            : p,
        ),
      );
    } catch (e: any) {
      if (cancelledUploadsRef.current.has(localId)) {
        cancelledUploadsRef.current.delete(localId);
        return;
      }
      setPendingFiles((prev) =>
        prev.map((p) =>
          p.localId === localId
            ? { ...p, uploading: false, error: e?.message || 'Upload failed' }
            : p,
        ),
      );
    }
  };

  const onPickFiles = (list: FileList | null) => {
    if (!list) return;
    const remaining = MAX_FEEDBACK_ATTACHMENTS - pendingFiles.length;
    if (remaining <= 0) {
      toast({
        title: 'Attachment limit',
        description: `You can attach up to ${MAX_FEEDBACK_ATTACHMENTS} files.`,
        variant: 'destructive',
      });
      return;
    }
    const files = Array.from(list).slice(0, remaining);
    for (const file of files) {
      // Match the file-input accept list (png/jpeg/webp/gif/pdf) — not any image/*.
      const allowed = FEEDBACK_ATTACHMENT_MIME_ALLOWLIST.has(file.type);
      if (!allowed) {
        toast({
          title: 'Unsupported file',
          description: `${file.name}: use PNG, JPEG, WebP, GIF, or PDF.`,
          variant: 'destructive',
        });
        continue;
      }
      if (file.size > MAX_FEEDBACK_ATTACHMENT_BYTES) {
        toast({
          title: 'File too large',
          description: `${file.name} exceeds the 5 MB limit.`,
          variant: 'destructive',
        });
        continue;
      }
      void uploadOne(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (localId: string) => {
    setPendingFiles((prev) => {
      const target = prev.find((p) => p.localId === localId);
      if (target?.uploadId) {
        // Best-effort cleanup so removed attachments do not orphan in storage.
        void fileUploadApi.deleteFile(target.uploadId).catch(() => undefined);
      } else if (target?.uploading) {
        cancelledUploadsRef.current.add(localId);
      }
      return prev.filter((p) => p.localId !== localId);
    });
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || submit.isPending) return;

    const structured: Record<string, string> = {};
    if (type === 'BUG') {
      if (stepsToReproduce.trim()) structured.stepsToReproduce = stepsToReproduce.trim();
      if (expected.trim()) structured.expected = expected.trim();
      if (actual.trim()) structured.actual = actual.trim();
    } else if (whyItMatters.trim()) {
      structured.whyItMatters = whyItMatters.trim();
    }

    const meta = captureFeedbackMetadata(structured);
    const attachmentIds = pendingFiles
      .map((f) => f.uploadId)
      .filter((id): id is string => Boolean(id));

    submit.mutate(
      {
        type,
        title: title.trim(),
        description: description.trim(),
        severity: type === 'BUG' ? severity : undefined,
        attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
        ...meta,
      },
      {
        onSuccess: () => {
          onSubmitted();
          navigate('/dashboard');
        },
      },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4 mt-4">
      <div>
        <Label className="text-label-bold mb-1 block">Type</Label>
        <Select value={type} onValueChange={(v) => setType(v as FeedbackType)}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="SUGGESTION">Suggestion</SelectItem>
            <SelectItem value="BUG">Bug</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-label-bold mb-1 block">Title</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Short summary"
          maxLength={200}
          required
        />
      </div>

      <div>
        <Label htmlFor="feedback-description" className="text-label-bold mb-1 block">Description</Label>
        <Textarea
          id="feedback-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={
            type === 'BUG'
              ? 'What happened? Summarize the issue.'
              : 'What would help?'
          }
          rows={4}
          maxLength={10000}
          required
        />
      </div>

      {type === 'BUG' ? (
        <>
          <div>
            <Label className="text-label-bold mb-1 block">Severity</Label>
            <Select value={severity} onValueChange={(v) => setSeverity(v as FeedbackSeverity)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="LOW">Low</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="CRITICAL">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="feedback-steps-to-reproduce" className="text-label-bold mb-1 block">Steps to reproduce</Label>
            <Textarea
              id="feedback-steps-to-reproduce"
              value={stepsToReproduce}
              onChange={(e) => setStepsToReproduce(e.target.value)}
              placeholder={"1. …\n2. …"}
              rows={3}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="feedback-expected" className="text-label-bold mb-1 block">Expected</Label>
              <Textarea
                id="feedback-expected"
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
                placeholder="What should happen"
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="feedback-actual" className="text-label-bold mb-1 block">Actual</Label>
              <Textarea
                id="feedback-actual"
                value={actual}
                onChange={(e) => setActual(e.target.value)}
                placeholder="What happened instead"
                rows={2}
              />
            </div>
          </div>
        </>
      ) : (
        <div>
          <Label htmlFor="feedback-why-it-matters" className="text-label-bold mb-1 block">Why it matters</Label>
          <Textarea
            id="feedback-why-it-matters"
            value={whyItMatters}
            onChange={(e) => setWhyItMatters(e.target.value)}
            placeholder="Optional — who benefits and how"
            rows={2}
          />
        </div>
      )}

      <div>
        <Label htmlFor="feedback-attachments" className="text-label-bold mb-1 block">
          Attachments
          <span className="ml-2 font-normal text-foreground-soft normal-case tracking-normal">
            (images or PDF, up to {MAX_FEEDBACK_ATTACHMENTS} × 5 MB)
          </span>
        </Label>
        <input
          id="feedback-attachments"
          ref={fileInputRef}
          type="file"
          accept={FEEDBACK_ATTACHMENT_ACCEPT}
          multiple
          className="block w-full text-sm text-foreground-soft file:mr-3 file:border file:border-border-subtle file:bg-transparent file:px-3 file:py-1.5 file:text-xs file:font-bold file:uppercase file:tracking-wider"
          onChange={(e) => onPickFiles(e.target.files)}
          disabled={pendingFiles.length >= MAX_FEEDBACK_ATTACHMENTS || submit.isPending}
        />
        {pendingFiles.length > 0 && (
          <ul className="mt-2 space-y-1">
            {pendingFiles.map((pf) => (
              <li
                key={pf.localId}
                className="flex items-center justify-between gap-2 text-xs border border-border-subtle px-2 py-1.5"
              >
                <span className="truncate min-w-0">
                  {pf.file.name}
                  {pf.uploading && <span className="text-foreground-soft"> — uploading…</span>}
                  {pf.error && <span className="text-destructive"> — {pf.error}</span>}
                  {pf.uploadId && !pf.error && (
                    <span className="text-foreground-soft"> — ready</span>
                  )}
                </span>
                <button
                  type="button"
                  className="shrink-0 text-foreground-soft hover:text-foreground underline"
                  onClick={() => removeFile(pf.localId)}
                  disabled={submit.isPending}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-xs text-foreground-soft">
        We auto-capture the page you&apos;re on (including origin), your browser, viewport, and app
        version to help us reproduce and prioritize. No screen content is captured beyond what you
        write and attach here.
      </p>

      <div className="flex justify-end gap-2 pt-2 border-t-[1.5px] border-border-subtle">
        <Button variant="ghost" type="button" onClick={onSubmitted} disabled={submit.isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!valid || submit.isPending}>
          {submit.isPending ? 'Sending…' : 'Send feedback'}
        </Button>
      </div>
    </form>
  );
}

function MySubmissionsList() {
  const { data, isLoading, error } = useMyFeedback();

  if (isLoading) {
    return <p className="py-8 text-center text-sm text-foreground-soft">Loading…</p>;
  }
  if (error) {
    return <p className="py-8 text-center text-sm text-destructive">Could not load your submissions.</p>;
  }
  const items = data?.items ?? [];
  if (items.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-foreground-soft">
        No submissions yet. Switch to the Submit tab to file your first one.
      </p>
    );
  }

  return (
    <ul className="mt-4 max-h-[60vh] overflow-y-auto divide-y-[1.5px] divide-border-subtle">
      {items.map((it) => (
        <SubmissionRow key={it.id} item={it} />
      ))}
    </ul>
  );
}

function SubmissionRow({ item }: { item: MyFeedbackItem }) {
  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-public-sans text-sm font-semibold text-foreground truncate">{item.title}</p>
          <p className="text-xs text-foreground-soft mt-0.5">
            <span className="uppercase tracking-wider font-bold">{item.type}</span>
            {item.severity && <> · {item.severity}</>}
            <> · {new Date(item.createdAt).toLocaleDateString()}</>
            {typeof item.attachmentCount === 'number' && item.attachmentCount > 0 && (
              <> · {item.attachmentCount} file{item.attachmentCount === 1 ? '' : 's'}</>
            )}
          </p>
          {item.latestAnalysis && (
            <p className="text-xs text-foreground-soft mt-2 italic">
              AI: {item.latestAnalysis.summary}
            </p>
          )}
        </div>
        <StatusBadge status={item.status} />
      </div>
    </li>
  );
}

function StatusBadge({ status }: { status: FeedbackStatus }) {
  const variant: 'default' | 'success' | 'destructive' | 'secondary' | 'info' =
    status === 'DONE' ? 'success'
    : status === 'REJECTED' ? 'destructive'
    : status === 'IN_PROGRESS' ? 'info'
    : status === 'TRIAGED' ? 'secondary'
    : 'default';
  return <Badge variant={variant}>{status.replace('_', ' ')}</Badge>;
}
